import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Spinner } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { PSButton } from '@/components/ui/button';

import { useAppSelector } from '../../../../app/hook';
import { selectUseId } from '../../../../features/auth/auth.slice';
import { BlockType } from '../../../../interfaces/models/Knowledge/scenario.interface';
import HintBlock from '../../../common/HintBlock';
import AddNoteButton from '../AddNoteButton';
import DeleteButton from '../DeleteButton';
import NodeParentChanger from '../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../NoteForm';
import ScenarioSide from '../scenario/text-scenario/scenario-side';
import VideoScenarioDeleteButton from '../scenario/video-scenario/video-scenario-delete-button';
import ScenarioHeader from '../ScenarioHeader';
import { MacBotActionKind } from '../types';

interface IFormData extends INoteForm {
  title: string;
}

export const CheckIfUserExistsForm = ({
  setShow,
  itemData,
  tree,
  loading,
  deleteScenarioBlock,
  changeScenarioBlock,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(true);
  const userId = useAppSelector(selectUseId);

  const schema = Yup.object({
    title: Yup.string().max(30, t('SuperUserManager/blockTitleMaxLength')).required(t('SuperUserManager/blockTitleRequired')),
    ...getNoteSchema(t),
  });
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    control,
    getValues,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: 'Check if user exist',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        id: itemData.id,
        type: BlockType.CheckUserExists,
        parent: itemData.parent,
        title: data.title,
        notes: data.notes,
        block_id: itemData.block_id,
      };
      changeScenarioBlock(_data).then(() => {
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowCheckIfUserExists}
          onClose={() => {
            reset();
          }}
        />
        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Col xs={12}>
            <Form.Group>
              <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                {t('SuperUserManager/blockTitle')}
              </Form.Label>
              <Form.Control
                maxLength={31}
                max={31}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                isInvalid={!!errors.title}
                {...register(`title`)}
              />
              <HintBlock texts={[t('SuperUserManager/blockTitleMaxHint')]} />
              <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <div className="tw-h-px tw-border tw-border-solid tw-border-gray-200"></div>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {<VideoScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />}

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-user-exist-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
